import type { ExperimentNameUnion, ExperimentVersions } from '../types'
import { useCoreConfig } from '#imports'

/**
 * Looks up the version mapping for an experiment from the runtime config.
 *
 * @param experimentName - The experiment to look up
 * @returns The version mapping if found, or `undefined` if the experiment is not active
 *
 * @example
 * const versions = useExperimentVersions(ExperimentName.SIGNUP)
 * // versions?.a === '5_8_26'
 * // versions?.b === '5_9_26'
 */
export const useExperimentVersions = (experimentName: ExperimentNameUnion): ExperimentVersions | undefined => {
    const { experiments } = useCoreConfig()

    if (!experiments?.length) return

    return experiments?.find(experiment => experiment.name === experimentName)?.versions
}

/**
 * Whether an experiment is an actual A/B test: at least two versions configured.
 * With a single version every visitor gets that variant, so no test is running
 * and no experiment events should be sent.
 *
 * @param experimentName - The experiment to look up
 * @returns `true` when two or more versions are configured
 *
 * @example
 * const isAbTest = useIsAbTest(ExperimentName.LANDING_PAGE)
 * // versions { a: '1_25_26', b: '1_26_26' } → true
 * // versions { b: '1_26_26' } → false
 */
export const useIsAbTest = (experimentName: ExperimentNameUnion): boolean => {
    return Object.keys(useExperimentVersions(experimentName) ?? {}).length >= 2
}
